import {SerializedStyles} from '@emotion/react';
import {css} from '@mui/material';
import {TooltipWrapperCore} from '@wallaby-ui/core';
import * as React from 'react';
import {useLayoutEffect, useState} from 'react';

type Props = {
  text: string;
  /** The cell's own look (font, colour, muted state), on top of the one-line ellipsis. */
  textCss?: SerializedStyles;
} & Record<`data-${string}`, string | boolean | undefined>;

const wrapperCss = css`
  display: block;
  max-width: 100%;
`;
const ellipsisCss = css`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/**
 * One line of table text that ends with an ellipsis when it does not fit its column, never running into the next one, and
 * shows the full text in a tooltip only then (PDW-410). Re-measured whenever the column width changes.
 */
export const AlertOverflowText: React.FC<Props> = (props) => {
  const {text, textCss, ...dataProps} = props;
  const [element, setElement] = useState<HTMLSpanElement | null>(null);
  const [truncated, setTruncated] = useState(false);

  useLayoutEffect(() => {
    if (!element) return undefined;
    const measure = () => setTruncated(element.scrollWidth > element.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [element, text]);

  return (
    <TooltipWrapperCore tooltip={truncated ? text : undefined} noPadding maxWidth={400} wrapperCss={wrapperCss}>
      <span ref={setElement} css={[ellipsisCss, textCss]} {...dataProps}>
        {text}
      </span>
    </TooltipWrapperCore>
  );
};

AlertOverflowText.displayName = 'AlertOverflowText';
